import React, { useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import './LegalPage.css';

function PrivacyPolicy({ user, signIn }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="legal-page">
      <Header user={user} signIn={signIn} />
      <main className="legal-content" data-testid="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: August 1, 2026</p>

        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how Tweedia Technologies LLC ("Tweedia," "we," "us,"
          or "our") collects, uses, discloses, and otherwise processes personal
          information in connection with Tweedia Studio and any related services,
          applications, and websites (collectively, "the Service"). By explicitly
          accepting our Terms of Service after signing in, you acknowledge that you
          have read and understood this Privacy Policy.
        </p>

        <h2>2. Information We Collect</h2>
        <p>
          The categories of personal data we process include: (a) identifiers (such
          as name, email address, and account ID); (b) commercial information (such
          as purchase and credit history); (c) internet or other network activity
          information (such as device, browser, and usage data); (d) geolocation
          data (approximate, derived from IP address); (e) audio, electronic,
          visual, or similar information (such as videos and images you upload);
          and (f) inferences drawn from the above (such as motion analysis metrics
          derived from your content). We do not knowingly process sensitive personal
          data, biometric identifiers, or precise geolocation.
        </p>

        <h3>2.1 Information You Provide</h3>
        <ul>
          <li>
            <strong>Account information:</strong> name, email address, and profile
            photo provided through Google Sign-In or other authentication methods we
            may offer.
          </li>
          <li>
            <strong>User Content:</strong> videos, images, audio files, and any other
            media you upload to the Service, along with associated metadata (file
            names, timestamps, resolution, duration, file size).
          </li>
          <li>
            <strong>Payment information:</strong> billing details processed by our
            third-party payment processor, Stripe, Inc. We do not store full payment
            card numbers on our servers.
          </li>
          <li>
            <strong>Communications:</strong> information you provide when you contact
            us for support or otherwise communicate with us.
          </li>
          <li>
            <strong>Consent records:</strong> timestamps and details of consents you
            provide (e.g., Terms of Service acceptance and ML training consent
            after sign in).
          </li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <ul>
          <li>Device and browser information (type, operating system, browser version)</li>
          <li>IP address and approximate geographic location</li>
          <li>Usage data (pages visited, features used, actions taken within the Service)</li>
          <li>Error and performance diagnostics</li>
          <li>Cookies and similar tracking technologies (see Section 10)</li>
        </ul>

        <h3>2.3 Information Derived from Your Content</h3>
        <ul>
          <li>
            Motion analysis data, frame-level metrics, and video characteristics
            generated by our processing algorithms when analyzing your uploaded content.
          </li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li>To provide, operate, maintain, and improve the Service</li>
          <li>To process and deliver your video editing results</li>
          <li>To process payments and subscriptions, manage credits, and administer your account</li>
          <li>
            To communicate with you about the Service, including transactional and
            service emails such as subscription and billing notices, photo-expiry
            warning emails, and support responses
          </li>
          <li>To detect, investigate, and prevent fraud, abuse, and security incidents</li>
          <li>To comply with legal obligations and enforce our Terms of Service</li>
          <li>
            <strong>To train, develop, and improve machine learning models, artificial
            intelligence systems, and related technologies</strong> — including but not
            limited to computer vision models, video processing algorithms, motion
            detection systems, automated editing features, content classification and
            recommendation tools, and new AI-powered capabilities (see Section 6 for
            detailed disclosure)
          </li>
        </ul>

        <h2>4. Legal Bases for Processing (EEA/UK Users)</h2>
        <p>
          If you are located in the European Economic Area or the United Kingdom,
          we rely on the following legal bases:
        </p>
        <ul>
          <li>
            <strong>Performance of a contract:</strong> processing necessary to
            provide you with the Service under our Terms of Service.
          </li>
          <li>
            <strong>Consent:</strong> for ML/AI training use of your User Content
            (which you may grant or withhold after sign up, and withdraw at any time
            per Section 6).
          </li>
          <li>
            <strong>Legitimate interests:</strong> for analytics, fraud prevention,
            service improvement, and security, where those interests are not
            overridden by your rights.
          </li>
          <li>
            <strong>Legal obligation:</strong> where processing is required by
            applicable law.
          </li>
        </ul>

        <h2>5. How We Share Your Information</h2>
        <p>
          We do not sell your personal information, and we do not share it for
          targeted advertising or profiling that produces legal or similarly
          significant effects. We disclose personal data to the following
          categories of third parties:
        </p>
        <ul>
          <li><strong>Cloud infrastructure and hosting providers</strong> (e.g., Google Cloud Platform, Firebase)</li>
          <li><strong>Authentication providers</strong> (e.g., Google Sign-In)</li>
          <li><strong>Payment processors</strong> (e.g., Stripe, Inc.)</li>
          <li><strong>Video processing and compute providers</strong> used to render your outputs</li>
          <li><strong>Analytics and diagnostics providers</strong> (e.g., Google Analytics via Firebase, error monitoring)</li>
          <li><strong>Communications providers</strong> used to send transactional and support emails</li>
          <li><strong>Professional advisors</strong> (e.g., counsel, accountants, auditors) under confidentiality obligations</li>
          <li><strong>Governmental authorities and law-enforcement</strong> where required by law (see below)</li>
        </ul>
        <p>We share information only as follows:</p>
        <ul>
          <li>
            <strong>Service providers:</strong> third parties in the categories above
            who perform services on our behalf. These providers are contractually
            obligated to use your information only as necessary to provide services
            to us.
          </li>
          <li>
            <strong>Legal requirements:</strong> when required by applicable law,
            regulation, legal process, or governmental request.
          </li>
          <li>
            <strong>Protection of rights:</strong> to enforce our Terms of Service,
            protect our rights, privacy, safety, or property, or that of our users
            or the public.
          </li>
          <li>
            <strong>Business transfers:</strong> in connection with a merger,
            acquisition, reorganization, or sale of all or a portion of our assets,
            in which case your information may be transferred as part of such
            transaction. We will notify you of any such change.
          </li>
        </ul>

        <h2>6. Use of Your Data for Machine Learning and AI Development</h2>
        <p>
          This section provides detailed information about how we may use your
          User Content for machine learning and AI purposes.
        </p>

        <h3>6.1 What We May Use</h3>
        <p>
          With your consent, we may use content you upload to the Service — including
          videos, images, audio files, and associated metadata — to train and improve
          our machine learning models and AI-powered features.
        </p>

        <h3>6.2 How and Why We Use It</h3>
        <p>Specifically, your content may be used for:</p>
        <ul>
          <li>Training and improving computer vision and video processing models</li>
          <li>Developing and refining motion detection and stillness analysis algorithms</li>
          <li>Improving automated editing, transition, and content enhancement features</li>
          <li>Building content classification and recommendation systems</li>
          <li>Developing new AI-powered tools and capabilities within the Service</li>
          <li>Research and development of general-purpose video understanding models</li>
        </ul>

        <h3>6.3 Scope of Use</h3>
        <p>
          Models trained using your content may be used to power features within the
          Service, and may also be used in connection with other products and services
          we develop or offer. Trained models do not contain identifiable copies of
          your content; rather, they contain statistical patterns derived from
          aggregated training data.
        </p>

        <h3>6.4 Consent and Opt-Out</h3>
        <p>
          ML training use of your content is subject to your consent. After signing
          in and accepting our Terms of Service, you will be presented with a consent
          prompt indicating whether your uploads may be used for ML training. You may
          decline or opt out, and your choice applies to all future uploads until you
          change it in Account Settings.
        </p>
        <p>
          <strong>To withdraw consent or opt out globally:</strong> you may toggle the
          "AI &amp; Machine Learning Training" setting in the{' '}
          <strong>Privacy &amp; Data</strong> section of your{' '}
          <a href="/account">Account Settings</a> at any time, or contact us at{' '}
          <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a> to request
          that your content no longer be used for future ML training. Please note:
        </p>
        <ul>
          <li>
            Opting out does not affect content that has already been incorporated into
            trained models, as individual contributions cannot be extracted from a
            trained model after training is complete.
          </li>
          <li>
            Opting out does not affect your ability to use the Service — ML training
            consent is not required to access any features of the Service.
          </li>
          <li>
            We will honor opt-out requests within thirty (30) days of receipt and will
            cease using your content for new training processes going forward.
          </li>
        </ul>

        <h3>6.5 De-identification</h3>
        <p>
          Where practicable, we de-identify or aggregate content before using it for
          ML training. Training datasets may be processed to remove or obscure
          personally identifiable information such as faces, license plates, or
          visible text.
        </p>

        <h2>7. Data Retention</h2>
        <ul>
          <li>
            <strong>User Content:</strong> your projects, and the videos we produce
            from them, are retained while your account is active and the project
            exists. Raw source photos you upload (and the thumbnails generated from
            them) are working inputs, not long-term storage, and how long we keep them
            depends on your plan. On the Free plan, we automatically remove raw source
            photos from active storage 30 days after a project's last activity (an
            upload to, or a video render from, that project), and we send an advance
            warning email before doing so. On a paid subscription plan, raw source
            photos are retained while your subscription is active and are not subject
            to the 30-day inactivity removal above; this persistence lasts for the life
            of your subscription, and we do not apply any other absolute deletion
            deadline to them while you remain subscribed. If a subscription is
            cancelled, downgraded to Free, or lapses without being restored, your
            account's entitlements become the Free plan's from that point forward — this
            does not place your content in a read-only state and does not carry a grace
            period, and your raw source photos are not deleted for exceeding the Free
            plan's storage allowance. Instead, your raw source photos become subject to
            the same 30-day inactivity policy described above from that point forward.
            The compiled and edited videos produced from those photos are kept
            while the project exists. You are responsible for retaining your own copies
            of any originals you wish to keep. You may also delete individual projects
            at any time, and we will delete the associated content from our active
            storage within a reasonable period. Backup copies may persist for up to 90
            days. See Sections 4.5 and 6 of our Terms of Service for the related storage
            limits and subscription terms.
          </li>
          <li>
            <strong>Subscription and billing information:</strong> if you subscribe to a
            paid plan, we retain your subscription status, plan tier, billing cycle, and
            related transaction metadata for as long as your account is active and as
            required by applicable tax and financial regulations. Full payment card
            details are handled by our payment processor, Stripe, and are not stored on
            our systems (see the "Payment records" item below and Section 3).
          </li>
          <li>
            <strong>Account information:</strong> retained while your account is
            active and for up to 12 months after account deletion for legal,
            regulatory, and operational purposes.
          </li>
          <li>
            <strong>ML training data:</strong> content used for ML training may be
            retained in training datasets separately from your account. If you delete
            your account, we will stop using your content for future training, but
            models previously trained on your content are not affected. This is
            consistent with the license grant in our Terms of Service.
          </li>
          <li>
            <strong>Payment records:</strong> retained as required by applicable tax
            and financial regulations.
          </li>
        </ul>

        <h2>8. Your Rights and Choices</h2>

        <h3>8.1 All Users</h3>
        <p>You may:</p>
        <ul>
          <li>Access, update, or correct your account information at any time through your account settings</li>
          <li>Delete your account and associated data through your account settings</li>
          <li>Opt out of ML training use (see Section 6.4)</li>
          <li>Opt out of non-essential communications</li>
        </ul>

        <h3>8.2 California Residents (CCPA/CPRA)</h3>
        <p>If you are a California resident, you have the right to:</p>
        <ul>
          <li><strong>Know</strong> what personal information we collect, use, and disclose about you</li>
          <li><strong>Delete</strong> your personal information, subject to certain exceptions</li>
          <li><strong>Correct</strong> inaccurate personal information</li>
          <li><strong>Opt out of the sale or sharing of personal information</strong> — we do not sell or share personal information as defined by the CCPA/CPRA</li>
          <li><strong>Non-discrimination</strong> — we will not discriminate against you for exercising your privacy rights</li>
        </ul>
        <p>
          To exercise these rights, contact us at{' '}
          <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a>. We will
          verify your identity before processing your request.
        </p>

        <h3>8.3 Texas Residents (Texas Data Privacy and Security Act)</h3>
        <p>
          If you are a Texas resident, you have the following rights under the Texas
          Data Privacy and Security Act ("TDPSA," Tex. Bus. &amp; Com. Code Ch. 541):
        </p>
        <ul>
          <li><strong>Right to know / access:</strong> to confirm whether we process your personal data and to access that data</li>
          <li><strong>Right to correct</strong> inaccuracies in your personal data</li>
          <li><strong>Right to delete</strong> personal data we have collected from or about you</li>
          <li><strong>Right to data portability:</strong> to obtain a copy of personal data you previously provided to us in a portable, readily usable format</li>
          <li><strong>Right to opt out</strong> of (i) targeted advertising, (ii) the sale of personal data, and (iii) profiling in furtherance of decisions producing legal or similarly significant effects</li>
        </ul>
        <p>
          We do not sell personal data and we do not engage in targeted advertising
          or such profiling. We do not process sensitive personal data, and we do not
          knowingly process the personal data of a known child for targeted
          advertising, sale, or profiling.
        </p>
        <p>
          <strong>How to exercise your rights:</strong> email{' '}
          <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a> with your
          request. We will verify your identity using information already associated
          with your account and respond to your request within{' '}
          <strong>forty-five (45) days</strong> of receipt. We may extend this period
          by an additional 45 days when reasonably necessary and will notify you of
          any extension and the reason for it.
        </p>
        <p>
          <strong>Appeal process:</strong> if we decline to take action on your
          request, you may appeal that decision by replying to our decision email or
          by writing to{' '}
          <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a> with the
          subject line "TDPSA Appeal." We will respond in writing within{' '}
          <strong>sixty (60) days</strong> of receipt of the appeal, explaining the
          action taken or the reasons for declining to act. If your appeal is denied,
          you may contact the Texas Attorney General to submit a complaint at{' '}
          <a href="https://www.texasattorneygeneral.gov/consumer-protection/file-consumer-complaint" target="_blank" rel="noopener noreferrer">texasattorneygeneral.gov</a>.
        </p>

        <h3>8.4 Other U.S. State Privacy Laws</h3>
        <p>
          Residents of Virginia, Colorado, Connecticut, Utah, Oregon, Montana, and
          other states with comprehensive privacy laws may have additional rights,
          including the right to opt out of targeted advertising, sale of personal
          data, and profiling. We honor these rights consistent with applicable law.
          Contact us at <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a> to exercise your rights.
        </p>

        <h3>8.5 EEA and UK Residents</h3>
        <p>
          If you are located in the European Economic Area or the United Kingdom, you
          may have additional rights under the GDPR, including the right to access,
          rectification, erasure, restriction of processing, data portability, and the
          right to object to processing. You also have the right to lodge a complaint
          with your local supervisory authority.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          The Service is not directed to children under the age of 13 (or 16 in
          jurisdictions where a higher age of consent applies). We do not knowingly
          collect personal information from children under these ages. If we learn
          that we have collected personal information from a child below the applicable
          age, we will take steps to delete that information promptly. We do not use
          content uploaded by users under the age of 18 for ML training purposes.
        </p>

        <h2>10. Cookies and Similar Technologies</h2>
        <p>
          We use strictly necessary cookies and similar technologies to keep you
          signed in, maintain session state, and remember your preferences. We use
          analytics cookies (such as Google Analytics via Firebase) to understand
          how the Service is used. We do not use cookies for third-party advertising.
          You can manage cookie preferences through your browser settings.
        </p>

        <h2>11. Security</h2>
        <p>
          We implement industry-standard technical and organizational safeguards to
          protect your information, including encryption in transit (TLS) and at rest,
          access controls, and regular security assessments. Despite these measures,
          no method of transmission over the Internet or electronic storage is 100%
          secure, and we cannot guarantee absolute security.
        </p>
        <p>
          <strong>Breach notification:</strong> in the event of a breach of system
          security involving sensitive personal information, we will notify affected
          individuals and any applicable authorities as required by law, including
          the Texas Identity Theft Enforcement and Protection Act (Tex. Bus. &amp;
          Com. Code §521.053) for affected Texas residents.
        </p>

        <h2>12. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in the United States
          and other countries where our service providers operate. These countries may
          have data protection laws that differ from those in your jurisdiction. Where
          required by applicable law, we ensure appropriate safeguards are in place
          for such transfers.
        </p>

        <h2>13. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material
          changes, we will notify you by posting the updated policy on the Service
          with a new "Last updated" date, and where required by law, by email or
          other notice. Material changes to the Privacy Policy may require you to
          re-accept our Terms of Service through an in-app prompt. We encourage you
          to review this policy periodically.
        </p>

        <h2>14. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests related to this Privacy Policy
          or our data practices, please contact us at:
        </p>
        <p>
          Tweedia Technologies LLC<br />
          5900 Balcones Drive STE 100<br />
          Austin, Texas 78731<br />
          Email: <a href="mailto:privacy@tweediastudio.com">privacy@tweediastudio.com</a>
        </p>
        <p>
          For information about how we use the Service, please see our{' '}
          <a href="/terms">Terms of Service</a>.
        </p>
      </main>
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
